import { comissaoPendente, regraPagaZeroEmSilencio } from '../comissoes/completude'
import type { ComissaoPercentual, Produto, RegraComissao } from '../db/tipos'
import { converterNumeroBR } from '../lib/textoBR'
import { converterComissao } from './validacao'
import type { LinhaMapeada } from './validacao'

// Uma linha da planilha, ja reduzida ao que decide comissao. `produto` e o
// registro que JA existe no banco com esse codigo (null quando a linha vai
// criar o produto agora) -- sem ele nao da para saber se um percentual por
// linha/categoria ja cobre o produto.
export interface LinhaParaComissao {
  codigo: string
  descricao: string
  temPreco: boolean
  temComissao: boolean
  produto: Produto | null
}

// Chaveado por CODIGO, nao por produto_id: o passo cobra ao mesmo tempo de
// produto que ja existe e de produto que a planilha vai criar, e so o codigo
// existe nos dois. `produtoId` fica junto para o roteamento no envio.
export interface PendenteComissao {
  codigo: string
  descricao: string
  produtoId: string | null
}

// Traduz as linhas cruas da planilha para o que a decisao precisa saber.
// Mora aqui, e nao no componente, porque a conversao de TEXTO e onde os casos
// de borda vivem -- comissao ilegivel, preco zero, zero explicito -- e dentro
// de um componente com hook nada disso e testavel.
//
// Espelha a regra de preco de aoAplicar: vazio, ilegivel ou zero nao viram
// preco (`converterNumeroBR(texto) || null`). Sem preco na tabela o produto
// nao e vendavel por ela, e nao ha 0% a evitar.
export function linhasParaComissao(
  linhas: LinhaMapeada[],
  precosEditados: Record<string, string>,
  produtoPorCodigo: Map<string, Produto>,
): LinhaParaComissao[] {
  return linhas.map((l) => {
    const editado = precosEditados[l.campos.codigo]?.trim()
    const precoTexto = (editado ? editado : (l.campos.preco ?? '')).trim()
    return {
      codigo: l.campos.codigo,
      descricao: l.campos.descricao,
      temPreco: precoTexto !== '' && !!converterNumeroBR(precoTexto),
      // Zero EXPLICITO conta como definida: e escolha, nao esquecimento.
      // Texto ilegivel ('abc', '150') conta como AUSENTE -- converterComissao
      // devolve null e a importacao nao gravaria percentual nenhum.
      temComissao: converterComissao((l.campos.comissao ?? '').trim()) !== null,
      produto: produtoPorCodigo.get(l.campos.codigo) ?? null,
    }
  })
}

export type PassoComissao =
  | { acao: 'aplicar' }
  // A regra e '% fixo' com o percentual VAZIO: motor.ts faz `pct_padrao ?? 0`
  // e TUDO que ela governa paga 0%. Nao vira pendencia por produto porque
  // percentual por produto e inerte numa pct_fixo (ver completude.ts) -- o
  // unico remedio e digitar o % na tela da fornecedora.
  | { acao: 'avisar_regra_vazia' }
  | { acao: 'cobrar'; pendentes: PendenteComissao[] }

// O que a importacao tem de fazer antes de aplicar, para nenhum produto sair
// dela vendavel e sem comissao.
//
// Existe para responder a MESMA pergunta que planoDeComissaoDaTabela responde
// na tela da fornecedora. Antes a importacao refazia a conta a mao e conhecia
// so um dos estados ruins; os outros dois aplicavam em silencio:
//   A) sem regra nenhuma governando a tabela -> escolherRegra devolve undefined
//      na venda e o lancamento nem chega a nascer (lancamentos.ts:41)
//   B) regra '% fixo' com percentual vazio   -> tudo paga 0% (motor.ts:46)
//   C) produto NOVO da planilha              -> a checagem so olhava o banco
//
// A diferenca para planoDeComissaoDaTabela e a planilha: la o universo e o que
// esta gravado, aqui o universo inclui o que esta ENTRANDO.
export function passoDeComissaoDaImportacao(
  regra: RegraComissao | undefined,
  percentuais: ComissaoPercentual[],
  produtosComPreco: Produto[],
  linhas: LinhaParaComissao[],
): PassoComissao {
  const linhasComPreco = linhas.filter((l) => l.temPreco)

  if (regra && regra.tipo === 'pct_fixo') {
    // Sem nada precificado a tabela nao paga a ninguem: alarme ali so treinaria
    // a usuaria a ignorar o aviso (mesma razao de planoTabela.ts).
    const haPrecoEmJogo = produtosComPreco.length > 0 || linhasComPreco.length > 0
    if (haPrecoEmJogo && regraPagaZeroEmSilencio(regra)) return { acao: 'avisar_regra_vazia' }
    return { acao: 'aplicar' }
  }

  // Daqui para baixo: sem regra (tudo descoberto) ou pct_por_item.
  // O percentual que vem NA PLANILHA sera gravado por esta importacao, entao o
  // produto nao esta "sem comissao" -- pedi-lo de novo faria o valor digitado
  // no passo brigar com o da planilha no envio.
  const cobertoPelaPlanilha = new Set(
    linhas.filter((l) => l.temComissao).map((l) => l.codigo),
  )
  const pendentes: PendenteComissao[] = []
  const vistos = new Set<string>()

  const acrescentar = (codigo: string, descricao: string, produtoId: string | null) => {
    if (cobertoPelaPlanilha.has(codigo) || vistos.has(codigo)) return
    vistos.add(codigo)
    pendentes.push({ codigo, descricao, produtoId })
  }

  // 1) o que ja esta gravado com preco. Sem regra, TODO produto precificado
  //    esta descoberto -- e por isso o `regra &&` guarda o filtro.
  for (const produto of produtosComPreco) {
    if (regra && !comissaoPendente(regra, percentuais, produto)) continue
    acrescentar(produto.codigo, produto.descricao, produto.id)
  }

  // 2) o que a planilha esta trazendo com preco. Produto que ainda nao existe
  //    nao pode ter percentual, entao e sempre pendencia.
  for (const linha of linhasComPreco) {
    if (regra && linha.produto && !comissaoPendente(regra, percentuais, linha.produto)) continue
    acrescentar(linha.codigo, linha.descricao, linha.produto?.id ?? null)
  }

  return pendentes.length === 0 ? { acao: 'aplicar' } : { acao: 'cobrar', pendentes }
}

// Para onde vai cada percentual digitado no passo. Sao DOIS canais na edge
// function e um produto nunca pode ir pelos dois -- o upsert grava duas vezes:
//   - codigo na planilha  -> viaja em linhas[].comissao (mapeado por codigo la)
//   - fora da planilha    -> viaja em comissoes_existentes (por produto_id)
// O produto novo que ficou de fora da planilha nao tem canal nenhum: nao existe
// no banco e nao esta na linha. Por construcao ele nao acontece -- todo pendente
// sem produtoId veio da propria planilha.
export function rotearComissoes(
  pendentes: PendenteComissao[],
  valores: Record<string, string>,
  codigosDaPlanilha: Set<string>,
): { porCodigo: Record<string, string>; existentes: Array<{ produto_id: string; texto: string }> } {
  const porCodigo: Record<string, string> = {}
  const existentes: Array<{ produto_id: string; texto: string }> = []
  for (const pendente of pendentes) {
    const texto = valores[pendente.codigo]
    if (texto === undefined) continue
    if (codigosDaPlanilha.has(pendente.codigo)) porCodigo[pendente.codigo] = texto
    else if (pendente.produtoId) existentes.push({ produto_id: pendente.produtoId, texto })
  }
  return { porCodigo, existentes }
}
